import React, { useState, useEffect } from 'react';
// Import the competition type from the original types
import { Competition as OriginalCompetition} from '../../types/competition';
import { createFeedItemFromCompetition } from '../../services/firebase/feed-helpers';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import logger from '../../utils/logger';

// Define a more flexible competition type that can handle both the store and original formats
type FlexibleCompetition = OriginalCompetition & {
  [key: string]: any; // Allow any additional properties
};

interface ShareCompetitionProps {
  competition: FlexibleCompetition;
  onClose: () => void;
  onSuccess?: () => void;
}

const ShareCompetition: React.FC<ShareCompetitionProps> = ({ competition, onClose, onSuccess }) => {
  const [isPublic, setIsPublic] = useState<boolean>(competition.isPublic ?? true);
  const [shownOnFeed, setShownOnFeed] = useState<boolean>(true);
  const [sharing, setSharing] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Reset states when competition changes
    setIsPublic(competition.isPublic ?? true);
    setShownOnFeed(true);
    setSuccess(false);
    setError(null);
  }, [competition]);

  const handleShare = async () => {
    if (!user) {
      setError('You must be logged in to share a competition');
      return;
    }

    try {
      setSharing(true);
      setError(null);

      // Share the competition to feed
      await createFeedItemFromCompetition(
        competition,
        {
          displayName: user.displayName || 'Anonymous',
          username: user.username,
          photoURL: user.photoURL
        },
        {
          isPublic,
          shownOnFeed
        }
      );

      setSuccess(true);
      setSharing(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error sharing competition:', err);
      setError('Failed to share competition. Please try again.');
      setSharing(false);
    }
  };

  const handleNavigateToFeed = () => {
    navigate('/feed');
    onClose();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _getMedalEmoji = (rank: number): string => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '';
    }
  };

  // Format date for display
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Share Competition</h2>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mb-6">
        <div className="text-center mb-4">
          {competition.rank && competition.rank > 0 && (
            <span className="text-4xl" role="img" aria-label={`${competition.rank === 1 ? 'Gold' : competition.rank === 2 ? 'Silver' : 'Bronze'} medal`}>
              {competition.rank === 1 ? '🥇' : competition.rank === 2 ? '🥈' : '🥉'}
            </span>
          )}
        </div>
        
        <h3 className="text-center font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {competition.tournamentName}
        </h3>
        
        <p className="text-sm text-center text-gray-600 dark:text-gray-400">
          {formatDate(competition.date)}
          {competition.location && ` at ${typeof competition.location === 'object' ? competition.location.name : competition.location}`}
        </p>
        
        <div className="mt-3 bg-gray-100 dark:bg-gray-700 rounded-md p-3">
          <div className="grid grid-cols-3 text-center">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">MATCHES</p>
              <p className="font-semibold">{(competition.winsCount || 0) + (competition.lossesCount || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">WINS</p>
              <p className="font-semibold">{competition.winsCount || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">LOSSES</p>
              <p className="font-semibold">{competition.lossesCount || 0}</p>
            </div>
          </div>
          
          {competition.rank && competition.rank > 0 && (
            <div className="mt-2 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">MEDAL</p>
              <p className="font-semibold capitalize">{competition.rank === 1 ? 'Gold' : competition.rank === 2 ? 'Silver' : 'Bronze'}</p>
            </div>
          )}
        </div>
        
        {competition.note && (
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 italic">
            "{competition.note}"
          </p>
        )}
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-700 dark:text-gray-200">Share on feed</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={shownOnFeed}
              onChange={() => setShownOnFeed(!shownOnFeed)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-700 dark:text-gray-200">Public (visible to everyone)</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={isPublic}
              onChange={() => setIsPublic(!isPublic)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {success ? (
        <div className="text-center">
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
            Competition shared successfully!
          </div>
          <button
            onClick={handleNavigateToFeed}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-full"
          >
            Go to Feed
          </button>
        </div>
      ) : (
        <button
          onClick={handleShare}
          disabled={sharing || (!shownOnFeed && !isPublic)}
          className={`px-6 py-2 rounded-md w-full transition-colors ${
            sharing
              ? 'bg-gray-400 text-gray-700'
              : !shownOnFeed && !isPublic
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {sharing ? 'Sharing...' : 'Share Competition'}
        </button>
      )}
    </div>
  );
};

export default ShareCompetition;